import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Server-side lazy loading for a `DataTable`, driven by an IntersectionObserver
 * sentinel (DCP-324).
 *
 * WHY THIS EXISTS AT ALL. `@atrvd/saas-ui` has no infinite-scroll or
 * virtualisation support — there is no `onLoadMore`, no `hasMore`, no windowing
 * anywhere in the package, and no precedent for it in DCP either; every other
 * table here uses the library's page-number `pagination`. The per-connection
 * accounts table cannot: a single bank login routinely exposes 100+ accounts and
 * the screen MUTATES the very columns it filters and sorts on, so offset paging
 * would skip and repeat rows as the user works (which is why the data layer is
 * keyset-based). So the loading trigger is built here, once, out of public props
 * only.
 *
 * WHAT IS DELIBERATELY NOT DONE:
 *
 *   - `DataTable` is NOT forked or wrapped. The sentinel is rendered as ordinary
 *     content inside a synthetic last row via `renderFullWidthRow`.
 *   - No `stickyHeader` + `maxHeight`. That pair turns the table into its own
 *     scroll container, and a scroll container nested in the page's is a bug
 *     this project has fixed four times (DCP-313/312/336/332). The PAGE scrolls;
 *     the observer therefore watches the viewport (`root: null`), which is what
 *     makes a bare sentinel work at all.
 *   - No new dependency. `IntersectionObserver` is available in every browser
 *     the product supports.
 *
 * KNOWN CONSEQUENCE, accepted rather than worked around: because only fetched
 * pages exist client-side, the table's "select all" checkbox covers ONLY THE
 * ROWS LOADED SO FAR, not every row matching the filter. A bulk action therefore
 * applies to what the user can actually see and scroll to. Making it mean "all
 * N matches" would require a server-side bulk-by-filter path that the data layer
 * does not have (its mutations take explicit id arrays), and silently acting on
 * unseen rows is worse than a smaller, visible selection.
 */

/** What {@link useInfiniteScroll} needs to know about the paging state. */
export interface UseInfiniteScrollOptions {
  /** True while another page exists to fetch. */
  hasMore: boolean
  /** True while a fetch is in flight (initial load OR next page). */
  isFetching: boolean
  /** Ask for the next page. Called at most once per page (see below). */
  onLoadMore: () => void
  /**
   * How far ahead of the sentinel to start loading, as an IntersectionObserver
   * `rootMargin`. Defaults to 200px so the next page is usually in place before
   * the user reaches the bottom.
   */
  rootMargin?: string
}

/** What {@link useInfiniteScroll} hands back. */
export interface UseInfiniteScrollResult {
  /**
   * Ref callback to attach to the sentinel element. A CALLBACK ref, not an
   * object ref: the sentinel lives inside a table row that mounts and unmounts
   * as pages arrive, and only a callback ref is notified of that.
   */
  sentinelRef: (node: HTMLElement | null) => void
  /** True when a sentinel should be rendered at all. */
  enabled: boolean
}

/**
 * Fire `onLoadMore` when a sentinel element scrolls into view.
 *
 * EXACTLY ONE FETCH PER PAGE. The observer fires on every intersection change
 * — and a sentinel that stays in view after a short page arrives keeps firing —
 * so a naive handler requests the same page several times. Two guards prevent
 * that:
 *
 *   1. `isFetching` short-circuits the callback, so nothing is requested while a
 *      request is outstanding.
 *   2. A latch (`armedRef`) is closed on the first accepted intersection and
 *      only re-opened once `isFetching` has gone back to false. That closes the
 *      window between the click-equivalent (the intersection) and React Query
 *      actually flipping `isFetching` to true, which is a full render apart and
 *      is where the duplicate request would otherwise slip through.
 *
 * @param options - See {@link UseInfiniteScrollOptions}
 * @returns The sentinel ref callback and whether to render a sentinel
 */
export function useInfiniteScroll({
  hasMore,
  isFetching,
  onLoadMore,
  rootMargin = '200px',
}: UseInfiniteScrollOptions): UseInfiniteScrollResult {
  // The observed node, held in state so the effect below re-runs when the
  // sentinel row is re-created (a ref mutation alone would not re-run it).
  const [sentinel, setSentinel] = useState<HTMLElement | null>(null)

  // Latch: open = an intersection may request a page.
  const armedRef = useRef(true)
  // Latest callback/flags, read at observation time so the observer does not
  // have to be torn down and rebuilt whenever a render produces a new closure.
  const onLoadMoreRef = useRef(onLoadMore)
  const isFetchingRef = useRef(isFetching)
  const hasMoreRef = useRef(hasMore)
  onLoadMoreRef.current = onLoadMore
  isFetchingRef.current = isFetching
  hasMoreRef.current = hasMore

  // Re-arm once the in-flight fetch settles. Keyed on `isFetching` going false
  // rather than on the row count changing, so a page that came back EMPTY (the
  // keyset "full last page" case) also re-arms instead of wedging the latch.
  //
  // DECLARED BEFORE the observer effect on purpose: effects run in declaration
  // order within a commit, so by the time the observer below is re-created for
  // the same `isFetching` change the latch is already open — which is what lets
  // its immediate re-observation request the next page.
  useEffect(() => {
    if (!isFetching) armedRef.current = true
  }, [isFetching])

  // `isFetching` is a DEPENDENCY, which rebuilds the observer once per fetch
  // cycle. That is the point, not waste. An IntersectionObserver reports
  // CHANGES in intersection, and `observe()` on an element that is already
  // intersecting fires immediately — so re-observing after a fetch settles is
  // what keeps loading going when the sentinel never LEFT the viewport. Without
  // it, a page that does not push the sentinel past the root margin (a short
  // page, a tall viewport, a zoomed-out window) produces no further
  // intersection change and the list silently stops at that page until the user
  // nudges the scrollbar. The re-observe during the false→true transition is
  // harmless: the latch and `isFetchingRef` refuse it.
  useEffect(() => {
    if (!sentinel) return
    // Guard for jsdom and any environment without the API: no observer simply
    // means no auto-loading, never a crash.
    if (typeof IntersectionObserver === 'undefined') return

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return
        if (!armedRef.current || isFetchingRef.current || !hasMoreRef.current) return
        armedRef.current = false
        onLoadMoreRef.current()
      },
      // `root: null` = the viewport, because the page (not the table) scrolls.
      { root: null, rootMargin },
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [sentinel, rootMargin, isFetching])

  const sentinelRef = useCallback((node: HTMLElement | null) => setSentinel(node), [])

  return { sentinelRef, enabled: hasMore }
}

/**
 * Flatten paged results into one row array, dropping ids already seen.
 *
 * Keyset paging cannot normally repeat a row, but a page fetched either side of
 * a mutation can: rename an account while the table is sorted by name and its
 * new position may fall inside a page already loaded. React's list rendering
 * turns that into duplicate keys, so the accumulator — not the caller — is where
 * it is handled. First occurrence wins, which keeps rows in server order.
 *
 * @param pages - Pages in fetch order
 * @param getRows - Extract a page's rows
 * @param getId - Extract a row's identity
 * @returns One deduped array, in page order
 */
export function flattenPages<TPage, TRow>(
  pages: readonly TPage[] | undefined,
  getRows: (page: TPage) => readonly TRow[],
  getId: (row: TRow) => string,
): TRow[] {
  const seen = new Set<string>()
  const out: TRow[] = []
  for (const page of pages ?? []) {
    for (const row of getRows(page)) {
      const id = getId(row)
      if (seen.has(id)) continue
      seen.add(id)
      out.push(row)
    }
  }
  return out
}
